import React, { useEffect } from 'react';
import useScrollLock from '../../../hooks/useScrollLock';
import { VideoOff } from 'lucide-react';

interface SessionEndedModalProps {
    open: boolean;
    onOkay: () => void;
}

const SessionEndedModal: React.FC<SessionEndedModalProps> = ({ open, onOkay }) => {
    useScrollLock(open);

    const [isAnimating, setIsAnimating] = React.useState(false);

    useEffect(() => {
        if (open) {
            setTimeout(() => setIsAnimating(true), 10);
        } else {
            setIsAnimating(false);
        }
    }, [open]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop with blur */}
            <div
                className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isAnimating ? 'opacity-100' : 'opacity-0'
                    }`}
            />

            {/* Modal */}
            <div
                className={`relative bg-[#FFF8F0] rounded-2xl p-0 max-w-md w-full border border-gray-200/60 shadow-xl transform transition-all duration-300 ${isAnimating
                    ? 'opacity-100 scale-100 translate-y-0'
                    : 'opacity-0 scale-95 translate-y-4'
                    }`}
            >
                <div className="p-6">
                    {/* Icon and Content */}
                    <div className="flex flex-col items-center text-center pt-2 pb-4">
                        {/* Icon with animated background */}
                        <div
                            className={`relative mb-4 bg-red-50 border-2 border-red-200 rounded-full p-4 transition-all duration-500 ${isAnimating ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
                                }`}
                        >
                            <div className="absolute inset-0 rounded-full bg-white/50" />
                            <VideoOff className={`relative text-red-600 w-8 h-8`} strokeWidth={2.5} />
                        </div>

                        {/* Title */}
                        <h2
                            className={`text-gray-900 text-2xl font-bold mb-2 tracking-tight transition-all duration-500 delay-100 ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                                }`}
                        >
                            Session Ended
                        </h2>

                        {/* Message */}
                        <p
                            className={`text-gray-600 text-sm leading-relaxed max-w-sm transition-all duration-500 delay-200 ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                                }`}
                        >
                            The session has been ended by the host.
                        </p>
                    </div>

                    {/* Footer */}
                    <div
                        className={`flex justify-center pt-2 transition-all duration-500 delay-300 ${isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                            }`}
                    >
                        <button
                            onClick={onOkay}
                            className="px-8 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:scale-105 active:scale-95"
                        >
                            Okay
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SessionEndedModal;
